import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import track from '../../lib/track'
import leadAttribution from '../../lib/attribution'
import Layout from '../../components/layout/layout'
import Seo, { breadcrumbJsonLd, organizationJsonLd } from '../../components/Seo/Seo'
import { LanguageContext } from '../../components/LanguageSwitcher/LanguageContext'
import useEntryMotion from '../../components/motion/useEntryMotion'
import checkicon from '../../images/checkicon.svg'
import TrustStrip from '../../components/TrustStrip/TrustStrip'
import {
  PROJECT_TYPES, INDUSTRIES, PLATFORMS, STAGES, DATA_STATES, UI_LEVELS, QA_LEVELS, PRIORITIES,
  DEFAULT_RATE, CURRENCIES, defaultCurrency, catalogFor, normaliseAiFeatures, estimate, formatMoney, formatRange,
} from '../../data/estimator'
import './estimator.scss'

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/myzkobag'
const STORAGE_KEY = 'mlmind:estimate'
const STEPS = ['project', 'scope', 'features', 'quality']

const INITIAL_INPUT = {
  projectType: '',
  description: '',
  industry: '',
  platforms: ['web'],
  stage: 'idea',
  dataState: 'raw',
  uiLevel: 'standard',
  qaLevel: 'standard',
  priority: 'balanced',
  rate: DEFAULT_RATE,
  currency: defaultCurrency(),
  features: [],
}

const ease = [0.22, 1, 0.36, 1]

/**
 * Project estimator.
 *
 * Four short steps, then a range: hours, weeks, budget, team, phases. The
 * numbers come from src/data/estimator.js and are the same for everyone who
 * gives the same answers. When the visitor writes a description, the AI
 * endpoint proposes a feature list tailored to it; if that endpoint is not
 * available the standard catalogue for the project type is used and the
 * visitor is told so. Either way the page works.
 *
 * Answers are kept in localStorage so a reload does not throw them away.
 */
const Estimator = () => {
  const { translations: t } = useContext(LanguageContext)
  const navigate = useNavigate()
  const { search } = useLocation()
  const animateIn = useEntryMotion()

  const [input, setInput] = useState(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null')
      if (saved && saved.input && saved.input.projectType) return { ...INITIAL_INPUT, ...saved.input }
    } catch (e) {
      // no saved state, or storage blocked
    }
    return INITIAL_INPUT
  })
  const [step, setStep] = useState(0) // 0..3 = steps, 4 = result
  const [ai, setAi] = useState({ status: 'idle', proposal: null, forKey: '' }) // idle | loading | done | failed
  const [lead, setLead] = useState({ name: '', email: '', company: '' })
  const [sendStatus, setSendStatus] = useState('idle')

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Service pages link here with ?type=rag etc. — start with that type
  // selected, unless the visitor is already mid-way through that same type.
  useEffect(() => {
    const wanted = new URLSearchParams(search).get('type')
    if (!wanted || !PROJECT_TYPES.some((x) => x.id === wanted)) return
    setInput((prev) => (prev.projectType === wanted ? prev : { ...INITIAL_INPUT, projectType: wanted, features: catalogFor(wanted) }))
  }, [search])

  useEffect(() => {
    if (step === 4 && result) track('estimate_completed', { type: input.projectType, hours: result.total[1] })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ input }))
    } catch (e) {
      // storage unavailable — the page still works for this visit
    }
  }, [input])

  const update = (patch) => setInput((prev) => ({ ...prev, ...patch }))

  const chooseType = (id) => {
    setInput((prev) => ({ ...prev, projectType: id, features: catalogFor(id) }))
    setAi({ status: 'idle', proposal: null, forKey: '' })
  }

  const togglePlatform = (p) =>
    setInput((prev) => {
      const has = prev.platforms.includes(p)
      const next = has ? prev.platforms.filter((x) => x !== p) : [...prev.platforms, p]
      return { ...prev, platforms: next.length ? next : prev.platforms }
    })

  const toggleFeature = (id) =>
    setInput((prev) => ({ ...prev, features: prev.features.map((x) => (x.id === id ? { ...x, on: !x.on } : x)) }))

  const [custom, setCustom] = useState('')
  const addCustom = (e) => {
    e.preventDefault()
    const name = custom.trim()
    if (!name) return
    setInput((prev) => ({
      ...prev,
      features: [...prev.features, { id: `custom-${Date.now()}`, name, hours: [24, 48], category: 'core', on: true, custom: true }],
    }))
    setCustom('')
  }

  // Ask the AI endpoint for a tailored feature list when the visitor reaches
  // the features step with a real description. Keyed on the description and
  // type so going back and forth does not re-request the same thing.
  const aiKey = `${input.projectType}|${input.description.trim()}`
  const requested = useRef('')
  useEffect(() => {
    if (step !== 2) return undefined
    if (input.description.trim().length < 40) return undefined
    if (requested.current === aiKey) return undefined
    requested.current = aiKey
    let cancelled = false
    setAi({ status: 'loading', proposal: null, forKey: aiKey })
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 45000)
    fetch('/api/estimate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
      body: JSON.stringify({
        description: input.description,
        projectType: input.projectType,
        industry: input.industry,
        platforms: input.platforms,
        stage: input.stage,
      }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((proposal) => {
        if (cancelled) return
        const features = normaliseAiFeatures(proposal.features)
        if (!features.length) throw new Error('empty')
        setAi({ status: 'done', proposal, forKey: aiKey })
        setInput((prev) => ({ ...prev, features }))
      })
      .catch(() => {
        if (cancelled) return
        setAi({ status: 'failed', proposal: null, forKey: aiKey })
        requested.current = '' // allow a retry if they edit the description
      })
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [step, aiKey, input.description, input.projectType, input.industry, input.platforms, input.stage])

  const type = PROJECT_TYPES.find((x) => x.id === input.projectType)
  const result = useMemo(() => estimate(input), [input])

  const canNext = step === 0 ? Boolean(input.projectType) : step === 2 ? input.features.some((x) => x.on) : true
  const next = () => {
    setStep((s) => Math.min(s + 1, 4))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  const back = () => {
    setStep((s) => Math.max(s - 1, 0))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  const restart = () => {
    setInput(INITIAL_INPUT)
    setAi({ status: 'idle', proposal: null, forKey: '' })
    requested.current = ''
    setStep(0)
    setSendStatus('idle')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const breakdownText = useCallback(() => {
    if (!result) return ''
    const lines = result.lines.map((l) => `- ${l.name}: ${formatRange(l.hours, 'h')}`).join('\n')
    return [
      `Project type: ${t[`estType_${input.projectType}`]}`,
      `Industry: ${input.industry ? t[`estInd_${input.industry}`] : '—'}`,
      `Platforms: ${input.platforms.map((p) => t[`estPlat_${p}`]).join(', ')}`,
      `Starting point: ${t[`estStage_${input.stage}`]}`,
      type && type.ml ? `Data: ${t[`estData_${input.dataState}`]}` : null,
      `Interface: ${t[`estUi_${input.uiLevel}`]} · Testing: ${t[`estQa_${input.qaLevel}`]} · Delivery: ${t[`estPri_${input.priority}`]}`,
      '',
      `Engineering hours: ${formatRange(result.total, 'h')}`,
      `Timeline: ${formatRange(result.weeks)} weeks with ${result.team} people`,
      `Budget at ${CURRENCIES[input.currency] || '$'}${result.rate}/h: ${formatMoney(result.cost[0], input.currency)} – ${formatMoney(result.cost[1], input.currency)}`,
      '',
      'Features:',
      lines,
      '',
      input.description ? `Description:\n${input.description}` : null,
      ai.proposal ? `\nAI summary: ${ai.proposal.summary}` : null,
    ]
      .filter((x) => x !== null)
      .join('\n')
  }, [result, input, t, type, ai.proposal])

  const send = async (e) => {
    e.preventDefault()
    if (sendStatus === 'sending') return
    setSendStatus('sending')
    try {
      const r = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          _subject: `Project estimate request from ${lead.name || 'the website'} (${formatRange(result.total, 'h')})`,
          name: lead.name,
          email: lead.email,
          company: lead.company,
          message: breakdownText(),
          source: 'Project estimator',
          ...leadAttribution(),
        }),
      })
      setSendStatus(r.ok ? 'sent' : 'error')
      if (r.ok) track('estimate_sent', { type: input.projectType })
    } catch (err) {
      setSendStatus('error')
    }
  }

  const panel = (key, children) => (
    <motion.div
      key={key}
      initial={animateIn ? { opacity: 0, y: 16 } : false}
      animate={{ opacity: 1, y: 0 }}
      exit={animateIn ? { opacity: 0, y: -12 } : undefined}
      transition={{ duration: 0.35, ease }}
    >
      {children}
    </motion.div>
  )

  const Choice = ({ options, value, onChange, prefix, descPrefix, multi }) => (
    <div className='est-choices'>
      {options.map((opt) => {
        const active = multi ? value.includes(opt) : value === opt
        return (
          <button
            key={opt}
            type='button'
            className={`est-choice ${active ? 'est-choice-active' : ''}`}
            onClick={() => onChange(opt)}
            aria-pressed={active}
          >
            <span className='est-choice-label'>{t[`${prefix}_${opt}`]}</span>
            {descPrefix && <span className='est-choice-desc'>{t[`${descPrefix}_${opt}`]}</span>}
          </button>
        )
      })}
    </div>
  )

  return (
    <Layout>
      <Seo
        title={t.estSeoTitle}
        description={t.estSeoDescription}
        jsonLd={[
          organizationJsonLd(),
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'Project estimator', path: '/estimate' },
          ]),
        ]}
      />

      <div className='est-page'>
        <div className='est-inner'>
          <p className='est-kicker'>{t.estKicker}</p>
          <h1 className='est-title'>{t.estTitle}</h1>
          <p className='est-sub'>{t.estSub}</p>
          <Link className='est-guide-link' to='/articles/aiDevelopmentCost'>{t.estGuideLink} →</Link>

          {/* Step rail */}
          <ol className='est-rail' aria-label={t.estStepsLabel}>
            {STEPS.map((s, i) => (
              <li key={s} className={`est-rail-item ${step === i ? 'est-rail-active' : ''} ${step > i ? 'est-rail-done' : ''}`}>
                <button type='button' onClick={() => step > i && setStep(i)} disabled={step <= i}>
                  <span className='est-rail-num'>{step > i ? '✓' : i + 1}</span>
                  <span>{t[`estStep_${s}`]}</span>
                </button>
              </li>
            ))}
            <li className={`est-rail-item ${step === 4 ? 'est-rail-active' : ''}`}>
              <button type='button' disabled>
                <span className='est-rail-num'>★</span>
                <span>{t.estStep_result}</span>
              </button>
            </li>
          </ol>

          <div className='est-card'>
            <AnimatePresence mode='wait'>
              {/* ---------- Step 1: project ---------- */}
              {step === 0 && panel('project', (
                <>
                  <h2 className='est-q'>{t.estTypeLabel}</h2>
                  <div className='est-types'>
                    {PROJECT_TYPES.map((pt) => (
                      <button
                        key={pt.id}
                        type='button'
                        className={`est-type ${input.projectType === pt.id ? 'est-type-active' : ''}`}
                        onClick={() => chooseType(pt.id)}
                        aria-pressed={input.projectType === pt.id}
                      >
                        <span className='est-type-name'>{t[`estType_${pt.id}`]}</span>
                        <span className='est-type-desc'>{t[`estTypeD_${pt.id}`]}</span>
                      </button>
                    ))}
                  </div>

                  <label className='est-field'>
                    <span className='est-q'>{t.estDescLabel} <em>{t.estOptional}</em></span>
                    <span className='est-hint'>{t.estDescHint}</span>
                    <textarea
                      rows={5}
                      maxLength={2500}
                      value={input.description}
                      placeholder={t.estDescPlaceholder}
                      onChange={(e) => update({ description: e.target.value })}
                    />
                    <span className='est-count'>{input.description.length}/2500</span>
                  </label>

                  <h2 className='est-q'>{t.estIndustryLabel} <em>{t.estOptional}</em></h2>
                  <Choice options={INDUSTRIES} value={input.industry} onChange={(v) => update({ industry: v === input.industry ? '' : v })} prefix='estInd' />
                </>
              ))}

              {/* ---------- Step 2: scope ---------- */}
              {step === 1 && panel('scope', (
                <>
                  <h2 className='est-q'>{t.estPlatformsLabel}</h2>
                  <Choice options={PLATFORMS} value={input.platforms} onChange={togglePlatform} prefix='estPlat' multi />

                  <h2 className='est-q'>{t.estStageLabel}</h2>
                  <Choice options={STAGES} value={input.stage} onChange={(v) => update({ stage: v })} prefix='estStage' descPrefix='estStageD' />

                  {type && type.ml && (
                    <>
                      <h2 className='est-q'>{t.estDataLabel}</h2>
                      <Choice options={DATA_STATES} value={input.dataState} onChange={(v) => update({ dataState: v })} prefix='estData' descPrefix='estDataD' />
                    </>
                  )}
                </>
              ))}

              {/* ---------- Step 3: features ---------- */}
              {step === 2 && panel('features', (
                <>
                  <h2 className='est-q'>{t.estFeaturesTitle}</h2>

                  {ai.status === 'loading' && (
                    <div className='est-ai-status est-ai-loading'>
                      <span className='est-spinner' aria-hidden='true' />
                      {t.estGenerating}
                    </div>
                  )}
                  {ai.status === 'done' && <p className='est-hint'>{t.estFeaturesAiNote}</p>}
                  {ai.status === 'failed' && <p className='est-hint est-hint-warn'>{t.estAiFailed}</p>}
                  {ai.status === 'idle' && <p className='est-hint'>{t.estFeaturesCatalogNote}</p>}

                  {ai.proposal && (
                    <div className='est-ai-box'>
                      <p className='est-ai-label'>{t.estSummaryLabel}</p>
                      <p className='est-ai-summary'>{ai.proposal.summary}</p>
                      {ai.proposal.risks && ai.proposal.risks.length > 0 && (
                        <>
                          <p className='est-ai-label'>{t.estRisksLabel}</p>
                          <ul className='est-ai-list'>{ai.proposal.risks.map((r) => <li key={r}>{r}</li>)}</ul>
                        </>
                      )}
                    </div>
                  )}

                  <ul className='est-features'>
                    {input.features.map((x) => (
                      <li key={x.id} className={`est-feature ${x.on ? 'est-feature-on' : ''}`}>
                        <label>
                          <input type='checkbox' checked={x.on} onChange={() => toggleFeature(x.id)} />
                          <span className='est-feature-check' aria-hidden='true' />
                          <span className='est-feature-body'>
                            <span className='est-feature-name'>{x.name}</span>
                            {x.description && <span className='est-feature-desc'>{x.description}</span>}
                          </span>
                          <span className='est-feature-hours'>{formatRange(x.hours)} {t.estHours}</span>
                        </label>
                      </li>
                    ))}
                  </ul>

                  <form className='est-add' onSubmit={addCustom}>
                    <input
                      type='text'
                      value={custom}
                      maxLength={90}
                      placeholder={t.estAddPlaceholder}
                      onChange={(e) => setCustom(e.target.value)}
                      aria-label={t.estAddFeature}
                    />
                    <button type='submit' className='est-btn est-btn-ghost'>{t.estAdd}</button>
                  </form>
                </>
              ))}

              {/* ---------- Step 4: quality & delivery ---------- */}
              {step === 3 && panel('quality', (
                <>
                  {!result?.apiOnly && (
                    <>
                      <h2 className='est-q'>{t.estUiLabel}</h2>
                      <Choice options={UI_LEVELS} value={input.uiLevel} onChange={(v) => update({ uiLevel: v })} prefix='estUi' descPrefix='estUiD' />
                    </>
                  )}
                  <h2 className='est-q'>{t.estQaLabel}</h2>
                  <Choice options={QA_LEVELS} value={input.qaLevel} onChange={(v) => update({ qaLevel: v })} prefix='estQa' descPrefix='estQaD' />

                  <h2 className='est-q'>{t.estPriorityLabel}</h2>
                  <Choice options={PRIORITIES} value={input.priority} onChange={(v) => update({ priority: v })} prefix='estPri' descPrefix='estPriD' />
                </>
              ))}

              {/* ---------- Result ---------- */}
              {step === 4 && result && panel('result', (
                <div className='est-result'>
                  <p className='est-kicker'>{t.estResultKicker}</p>
                  <h2 className='est-result-title'>{t.estResultTitle}</h2>

                  <div className='est-stats'>
                    <div className='est-stat'>
                      <span className='est-stat-label'>{t.estHoursLabel}</span>
                      <span className='est-stat-value'>{formatRange(result.total)}</span>
                      <span className='est-stat-unit'>{t.estHoursUnit}</span>
                    </div>
                    <div className='est-stat'>
                      <span className='est-stat-label'>{t.estWeeksLabel}</span>
                      <span className='est-stat-value'>{formatRange(result.weeks)}</span>
                      <span className='est-stat-unit'>{t.estWeeksUnit}</span>
                    </div>
                    <div className='est-stat est-stat-cost'>
                      <span className='est-stat-label'>{t.estCostLabel}</span>
                      <span className='est-stat-value'>{formatMoney(result.cost[0], input.currency)} – {formatMoney(result.cost[1], input.currency)}</span>
                      <span className='est-stat-unit'>
                        {t.estTeamLabel}: {result.team} {t.estPeople}
                      </span>
                    </div>
                  </div>

                  <label className='est-rate'>
                    <span>
                      <strong>{t.estRateLabel}</strong>
                      <em>{t.estRateHint}</em>
                    </span>
                    <span className='est-rate-input'>
                      <select
                        aria-label='Currency'
                        value={input.currency}
                        onChange={(e) => update({ currency: e.target.value })}
                      >
                        {Object.entries(CURRENCIES).map(([code, symbol]) => (
                          <option key={code} value={code}>{symbol} {code}</option>
                        ))}
                      </select>
                      <input
                        type='number'
                        min={15}
                        max={250}
                        value={input.rate}
                        onChange={(e) => update({ rate: e.target.value })}
                      />
                      /h
                    </span>
                  </label>

                  <div className='est-grid'>
                    <section>
                      <h3 className='est-h3'>{t.estPhasesLabel}</h3>
                      <ul className='est-bars'>
                        {result.phases.map((p) => (
                          <li key={p.id}>
                            <span className='est-bar-label'>{t[`estPhase_${p.id}`]}</span>
                            <span className='est-bar-track'><span className='est-bar-fill' style={{ width: `${Math.round(p.share * 100)}%` }} /></span>
                            <span className='est-bar-value'>{formatRange(p.hours, 'h')}</span>
                          </li>
                        ))}
                      </ul>
                    </section>

                    <section>
                      <h3 className='est-h3'>{t.estRolesLabel}</h3>
                      <ul className='est-roles'>
                        {result.roles.map((r) => (
                          <li key={r.id}>
                            <span>{t[`estRole_${r.id}`]}</span>
                            <span className='est-role-share'>{Math.round(r.share * 100)}%</span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  </div>

                  <details className='est-lines'>
                    <summary>{t.estLinesLabel}</summary>
                    <ul>
                      {result.lines.map((l) => (
                        <li key={l.key}><span>{l.name}</span><span>{formatRange(l.hours, 'h')}</span></li>
                      ))}
                      <li className='est-line-sub'><span>{t.estQaLine}</span><span>{formatRange(result.qa, 'h')}</span></li>
                      <li className='est-line-sub'><span>{t.estPmLine}</span><span>{formatRange(result.pm, 'h')}</span></li>
                      <li className='est-line-total'><span>{t.estHoursLabel}</span><span>{formatRange(result.total, 'h')}</span></li>
                    </ul>
                  </details>

                  <p className='est-disclaimer'>{t.estDisclaimer}</p>
                  <TrustStrip className='est-trust' />

                  {sendStatus === 'sent' ? (
                    <div className='est-sent'>
                      <img src={checkicon} alt='' width='36' height='36' />
                      <p>{t.estSent}</p>
                    </div>
                  ) : (
                    <form className='est-send' onSubmit={send}>
                      <div>
                        <h3 className='est-h3'>{t.estSendTitle}</h3>
                        <p className='est-hint'>{t.estSendSub}</p>
                      </div>
                      <div className='est-send-row'>
                        <input type='text' placeholder={t.quoteName} value={lead.name} autoComplete='name' required onChange={(e) => setLead({ ...lead, name: e.target.value })} />
                        <input type='email' placeholder={t.quoteEmail} value={lead.email} autoComplete='email' required onChange={(e) => setLead({ ...lead, email: e.target.value })} />
                        <input type='text' placeholder={t.readyCompany} value={lead.company} autoComplete='organization' onChange={(e) => setLead({ ...lead, company: e.target.value })} />
                      </div>
                      <div className='est-send-actions'>
                        <button type='submit' className='est-btn' disabled={sendStatus === 'sending'}>
                          {sendStatus === 'sending' ? t.quoteSending : t.estSendBtn}
                        </button>
                        <button type='button' className='est-btn est-btn-ghost' onClick={() => navigate('/contact-us')}>{t.estBookCall}</button>
                      </div>
                      {sendStatus === 'error' && <p className='est-hint est-hint-warn'>{t.estSendError}</p>}
                      <p className='est-note'>{t.quoteNote}</p>
                    </form>
                  )}
                </div>
              ))}
            </AnimatePresence>

            {/* Navigation */}
            <div className='est-nav'>
              {step > 0 && step < 4 && (
                <button type='button' className='est-btn est-btn-ghost' onClick={back}>← {t.estBack}</button>
              )}
              {step === 4 && (
                <>
                  <button type='button' className='est-btn est-btn-ghost' onClick={() => setStep(3)}>← {t.estEdit}</button>
                  <button type='button' className='est-btn est-btn-ghost' onClick={restart}>↻ {t.estRestart}</button>
                </>
              )}
              {step < 4 && (
                <button type='button' className='est-btn est-nav-next' onClick={next} disabled={!canNext || (step === 2 && ai.status === 'loading')}>
                  {step === 3 ? t.estSeeEstimate : t.estNext} <span>→</span>
                </button>
              )}
              {step < 4 && result && step > 0 && (
                <span className='est-running'>
                  {t.estRunningLabel}: <strong>{formatRange(result.total)} {t.estHours}</strong>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default Estimator
